import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Team Chat — Phase A (owner-approved 2026-08-14).
 *
 * Internal staff-to-staff messaging: 1:1 + group conversations, @mentions
 * with in-app notifications, unread counts. Gated by the matrix-editable
 * `teamchat.use` permission (internal roles by default) — customers and
 * vendors never see the surface.
 *
 * Delivery: polling (open thread 5s, conversation list 15s) — consistent
 * with the existing web-chat console; SSE is the Phase-B upgrade. Typing
 * `@` opens a teammate picker (mentioned members get an in-app ping);
 * ticket numbers like STYX-SRC2026… render as links into the ticket list.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessagesSquare, Plus, Send, Ticket as TicketIcon, Users as UsersIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Field, Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { t } from '@/lib/i18n';
import { formatRelative, getViewerTimezone } from '@/lib/timeDisplay';
const TICKET_RE = /\b[A-Z][A-Z0-9]{1,7}-[A-Z0-9]{2,}\d{3,}\b/g;
/** A referenced ticket rendered as a rich chip — lazily resolves the number
 * to the live ticket (number+title+status), cached per number. Falls back to
 * a search link while loading / if the number no longer resolves. */
function TicketRefChip({ number }) {
    const hit = useQuery({
        queryKey: ['teamchat-ticket-ref', number],
        queryFn: async () => {
            const res = await api.get(`/tickets?q=${encodeURIComponent(number)}&limit=5`);
            return res.items.find((i) => i.ticket_number === number) ?? null;
        },
        staleTime: 60_000,
    });
    const target = hit.data ? `/tickets/${hit.data.id}` : `/tickets?q=${encodeURIComponent(number)}`;
    return (_jsxs(Link, { to: target, className: "mx-0.5 inline-flex max-w-full items-center gap-1.5 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 align-middle text-xs font-medium text-sky-700 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300 dark:hover:bg-sky-950", title: hit.data ? `${hit.data.title} — ${hit.data.status.replace(/_/g, ' ')}` : number, children: [_jsx(TicketIcon, { size: 11, className: "shrink-0" }), number, hit.data && _jsx("span", { className: "max-w-[180px] truncate font-normal text-sky-600/80 dark:text-sky-400/80", children: hit.data.title }), hit.data && (_jsx("span", { className: "rounded bg-white/70 px-1 text-[10px] uppercase tracking-wide dark:bg-slate-900/50", children: hit.data.status.replace(/_/g, ' ') }))] }));
}
/** Render a message body: ticket numbers become links, @Names get accented. */
function MessageBody({ body }) {
    const parts = useMemo(() => {
        const out = [];
        let last = 0;
        for (const m of body.matchAll(TICKET_RE)) {
            if (m.index === undefined)
                continue;
            if (m.index > last)
                out.push(body.slice(last, m.index));
            out.push({ ticket: m[0] });
            last = m.index + m[0].length;
        }
        if (last < body.length)
            out.push(body.slice(last));
        return out;
    }, [body]);
    return (_jsx("span", { className: "whitespace-pre-wrap break-words", children: parts.map((p, i) => typeof p === 'string' ? (_jsx("span", { children: p.split(/(@[\w][\w .-]*)/g).map((seg, j) => seg.startsWith('@') ? (_jsx("span", { className: "font-medium text-brand-600 dark:text-brand-400", children: seg }, j)) : (_jsx("span", { children: seg }, j))) }, i)) : (_jsx(TicketRefChip, { number: p.ticket }, i))) }));
}
function NewChatModal({ open, onClose, onCreated }) {
    const qc = useQueryClient();
    const [picked, setPicked] = useState(new Set());
    const [title, setTitle] = useState('');
    const [filter, setFilter] = useState('');
    const colleagues = useQuery({
        queryKey: ['teamchat-colleagues'],
        queryFn: () => api.get('/team-chat/colleagues'),
        enabled: open,
        staleTime: 5 * 60_000,
    });
    const create = useMutation({
        mutationFn: () => api.post('/team-chat/conversations', {
            member_ids: [...picked],
            ...(title.trim() && { title: title.trim() }),
        }),
        onSuccess: (conv) => {
            void qc.invalidateQueries({ queryKey: ['teamchat-conversations'] });
            setPicked(new Set());
            setTitle('');
            onCreated(conv.id);
            onClose();
        },
    });
    const toggle = (id) => setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        return next;
    });
    const shown = (colleagues.data ?? []).filter((c) => !filter || c.display_name.toLowerCase().includes(filter.toLowerCase()));
    return (_jsx(Modal, { open: open, onClose: onClose, title: t('New chat'), children: _jsxs("div", { className: "space-y-3", children: [_jsx(Field, { label: t('Teammates'), children: _jsx(Input, { placeholder: t('Search teammates…'), value: filter, onChange: (e) => setFilter(e.target.value) }) }), _jsxs("div", { className: "max-h-56 space-y-0.5 overflow-y-auto rounded-md border border-slate-200 p-1 dark:border-slate-700", children: [shown.map((c) => (_jsxs("label", { className: "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800", children: [_jsx("input", { type: "checkbox", checked: picked.has(c.id), onChange: () => toggle(c.id) }), _jsx("span", { children: c.display_name }), _jsx("span", { className: "text-xs text-slate-400", children: c.email })] }, c.id))), shown.length === 0 && _jsx("div", { className: "px-2 py-3 text-sm text-slate-400", children: t('No teammates found.') })] }), picked.size > 1 && (_jsx(Field, { label: t('Group name (optional)'), children: _jsx(Input, { value: title, onChange: (e) => setTitle(e.target.value), maxLength: 120, placeholder: t('e.g. P1 war room') }) })), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { variant: "ghost", onClick: onClose, children: t('Cancel') }), _jsx(Button, { disabled: picked.size === 0 || create.isPending, onClick: () => create.mutate(), children: create.isPending ? t('Creating…') : t('Start chat') })] })] }) }));
}
export function TeamChatPage() {
    const qc = useQueryClient();
    // Deep link (2026-08-14): /team-chat?c=<conversation id> opens that thread
    // — used by the ticket page's "Discuss in Team Chat" card and by mention
    // notifications. Param is consumed once, then cleared from the URL.
    const [searchParams, setSearchParams] = useSearchParams();
    const [selected, setSelected] = useState(searchParams.get('c'));
    useEffect(() => {
        if (searchParams.get('c')) {
            const next = new URLSearchParams(searchParams);
            next.delete('c');
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [newOpen, setNewOpen] = useState(false);
    const [draft, setDraft] = useState('');
    // '@' typeahead: name → user id for every mention picked this draft; on
    // send only names still present in the text are submitted as mentions.
    const [mentionMap, setMentionMap] = useState({});
    const [mentionQuery, setMentionQuery] = useState(null);
    // '#' ticket picker (2026-08-14): typing # searches tickets and inserts a
    // reference that renders as a rich chip for everyone in the thread.
    const [ticketQuery, setTicketQuery] = useState(null);
    const inputRef = useRef(null);
    const bottomRef = useRef(null);
    const conversations = useQuery({
        queryKey: ['teamchat-conversations'],
        queryFn: () => api.get('/team-chat/conversations'),
        refetchInterval: 15_000,
    });
    const colleagues = useQuery({
        queryKey: ['teamchat-colleagues'],
        queryFn: () => api.get('/team-chat/colleagues'),
        staleTime: 5 * 60_000,
    });
    const messages = useQuery({
        queryKey: ['teamchat-messages', selected],
        queryFn: () => api.get(`/team-chat/conversations/${selected}/messages`),
        enabled: !!selected,
        refetchInterval: 5_000,
    });
    const markRead = useMutation({
        mutationFn: (id) => api.post(`/team-chat/conversations/${id}/read`, {}),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['teamchat-conversations'] });
            void qc.invalidateQueries({ queryKey: ['teamchat-unread'] });
        },
    });
    // Selecting a thread (or new messages arriving while it's open) marks it read.
    const msgCount = messages.data?.length ?? 0;
    useEffect(() => {
        if (selected && msgCount > 0)
            markRead.mutate(selected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, msgCount]);
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [msgCount]);
    const send = useMutation({
        mutationFn: () => {
            const mention_user_ids = Object.entries(mentionMap)
                .filter(([name]) => draft.includes(`@${name}`))
                .map(([, id]) => id);
            return api.post(`/team-chat/conversations/${selected}/messages`, {
                body: draft.trim(),
                mention_user_ids,
            });
        },
        onSuccess: () => {
            setDraft('');
            setMentionMap({});
            void qc.invalidateQueries({ queryKey: ['teamchat-messages', selected] });
            void qc.invalidateQueries({ queryKey: ['teamchat-conversations'] });
        },
    });
    const onDraftChange = (v) => {
        setDraft(v);
        // Live '@' detection: everything after the LAST '@' (until a comma /
        // end) is the typeahead query while the picker is relevant.
        const at = v.lastIndexOf('@');
        if (at >= 0 && (at === 0 || /\s/.test(v[at - 1] ?? ''))) {
            setMentionQuery(v.slice(at + 1));
        }
        else {
            setMentionQuery(null);
        }
        // Live '#' detection for the ticket picker — same shape as '@'.
        const hash = v.lastIndexOf('#');
        if (hash >= 0 && (hash === 0 || /\s/.test(v[hash - 1] ?? ''))) {
            setTicketQuery(v.slice(hash + 1));
        }
        else {
            setTicketQuery(null);
        }
    };
    const mentionMatches = mentionQuery !== null
        ? (colleagues.data ?? []).filter((c) => c.display_name.toLowerCase().startsWith(mentionQuery.toLowerCase())).slice(0, 6)
        : [];
    const pickMention = (c) => {
        const at = draft.lastIndexOf('@');
        setDraft(`${draft.slice(0, at)}@${c.display_name} `);
        setMentionMap((prev) => ({ ...prev, [c.display_name]: c.id }));
        setMentionQuery(null);
        inputRef.current?.focus();
    };
    const ticketHits = useQuery({
        queryKey: ['teamchat-ticket-search', ticketQuery],
        queryFn: async () => {
            const res = await api.get(`/tickets?q=${encodeURIComponent(ticketQuery ?? '')}&limit=6`);
            return res.items;
        },
        enabled: ticketQuery !== null && ticketQuery.length >= 2,
        staleTime: 30_000,
    });
    const pickTicket = (hit) => {
        const at = draft.lastIndexOf('#');
        setDraft(`${draft.slice(0, at)}${hit.ticket_number} `);
        setTicketQuery(null);
        inputRef.current?.focus();
    };
    const current = conversations.data?.find((c) => c.id === selected) ?? null;
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: t('Team Chat'), description: t('Internal messages with your teammates — mention with @ to notify.'), actions: _jsxs(Button, { onClick: () => setNewOpen(true), children: [_jsx(Plus, { size: 16 }), t('New chat')] }) }), _jsx(PageBody, { children: _jsxs("div", { className: "grid gap-4 lg:grid-cols-[300px_1fr]", children: [_jsx(Card, { children: _jsxs(CardBody, { className: "p-2", children: [(conversations.data ?? []).length === 0 && (_jsxs("div", { className: "p-4 text-center text-sm text-slate-400", children: [_jsx(MessagesSquare, { className: "mx-auto mb-2 h-6 w-6" }), t('No conversations yet — start one with New chat.')] })), _jsx("ul", { className: "space-y-0.5", children: (conversations.data ?? []).map((c) => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => setSelected(c.id), className: cn('w-full rounded-md px-3 py-2 text-left text-sm transition-colors', selected === c.id
                                                    ? 'bg-styx-50 text-styx-900 dark:bg-slate-800 dark:text-white'
                                                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'), children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsxs("span", { className: "flex min-w-0 items-center gap-1.5 font-medium", children: [c.ticket_number ? (_jsx(TicketIcon, { size: 13, className: "shrink-0 text-sky-500" })) : (c.is_group && _jsx(UsersIcon, { size: 13, className: "shrink-0 text-slate-400" })), _jsx("span", { className: "truncate", children: c.title })] }), c.unread_count > 0 && (_jsx("span", { className: "rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold text-styx-900", children: c.unread_count }))] }), c.last_message_preview && (_jsx("div", { className: "mt-0.5 truncate text-xs text-slate-400", children: c.last_message_preview }))] }) }, c.id))) })] }) }), _jsx(Card, { children: _jsx(CardBody, { className: "flex h-[calc(100vh-260px)] min-h-[360px] flex-col p-0", children: !current ? (_jsx("div", { className: "flex flex-1 items-center justify-center text-sm text-slate-400", children: t('Pick a conversation, or start a new chat.') })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm font-semibold dark:border-slate-800", children: [_jsx("span", { className: "min-w-0 truncate", children: current.title }), current.ticket_id && current.ticket_number && (_jsxs(Link, { to: `/tickets/${current.ticket_id}`, className: "inline-flex shrink-0 items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-700 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300 dark:hover:bg-sky-950", title: t('Open the ticket'), children: [_jsx(TicketIcon, { size: 11 }), current.ticket_number] })), current.is_group && !current.ticket_id && (_jsx("span", { className: "min-w-0 truncate text-xs font-normal text-slate-400", children: current.member_names.join(', ') }))] }), _jsxs("div", { className: "flex-1 space-y-3 overflow-y-auto px-4 py-3", children: [(messages.data ?? []).map((m) => (_jsxs("div", { className: "text-sm", children: [_jsxs("div", { className: "mb-0.5 flex items-baseline gap-2", children: [_jsx("span", { className: "font-semibold", children: m.author_name }), _jsx("span", { className: "text-[11px] text-slate-400", title: formatRelative(m.created_at, getViewerTimezone()).tooltip, children: formatRelative(m.created_at, getViewerTimezone()).display })] }), _jsx(MessageBody, { body: m.body })] }, m.id))), _jsx("div", { ref: bottomRef })] }), _jsxs("div", { className: "relative border-t border-slate-100 p-3 dark:border-slate-800", children: [ticketQuery !== null && (ticketHits.data?.length ?? 0) > 0 && (_jsx("div", { className: "absolute bottom-full left-3 z-10 mb-1 w-96 max-w-[90%] rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900", children: (ticketHits.data ?? []).map((h) => (_jsxs("button", { type: "button", className: "block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800", onClick: () => pickTicket(h), children: [_jsx("span", { className: "font-medium", children: h.ticket_number }), _jsx("span", { className: "ml-2 text-xs text-slate-400", children: h.title })] }, h.id))) })), mentionMatches.length > 0 && (_jsx("div", { className: "absolute bottom-full left-3 z-10 mb-1 w-64 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900", children: mentionMatches.map((c) => (_jsxs("button", { type: "button", className: "block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800", onClick: () => pickMention(c), children: ["@", c.display_name] }, c.id))) })), _jsxs("form", { className: "flex gap-2", onSubmit: (e) => {
                                                        e.preventDefault();
                                                        if (draft.trim() && !send.isPending)
                                                            send.mutate();
                                                    }, children: [_jsx(Input, { ref: inputRef, value: draft, onChange: (e) => onDraftChange(e.target.value), placeholder: t('Type a message — @ to mention, # for a ticket'), maxLength: 4000, className: "flex-1" }), _jsx(Button, { type: "submit", disabled: !draft.trim() || send.isPending, children: _jsx(Send, { size: 16 }) })] })] })] })) }) })] }) }), _jsx(NewChatModal, { open: newOpen, onClose: () => setNewOpen(false), onCreated: (id) => setSelected(id) })] }));
}
